/**
 * @file Frontend API client — expertDatasetService.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";
import type { CementBoxPlotSlotSelection } from "../components/DataVisualisation/CementBoxPlotChart";
import { formatCompoundUnitSymbol } from "../utils/formatUnitSymbol";
import { formatBackgroundGeoFromApi } from "../utils/backgroundGeographicalSpecificity";

/** Type definition — Expert Dataset. */
export interface ExpertDataset {
  id: string;
  reviewStatus?: string | null;
  submissionCode?: string;
  submittedDate?: string | null;
  createdAt?: string | null;
  background?: {
    materialId?: number;
    subMaterialId?: number;
    subMaterialIdName?: string | null;
    a1?: boolean;
    a2?: boolean;
    a3?: boolean;
    a1_a3?: boolean;
    dataReview?: string | null;
    dataCompleteness?: string | null;
    dataAcquisition?: string | null;
    geographicalCoverage?: string | null;
    geoRegion?: string | null;
    geographicScope?: string | null;
    country?: string | null;
    productionPlantState?: string | null;
    collectionStartYear?: string | null;
    collectionEndYear?: string | null;
    technologyId?: number | null;
    technologyIdName?: string | null;
    manufacturerId?: number | null;
    material?: {
      id?: number;
      name?: string;
      code?: string;
    };
  };
  contributorProfile?: {
    dataSource?: string | null;
  };
  contributor?: {
    name?: string | null;
    email?: string | null;
  } | null;
  a1Summary?: Record<string, unknown> | null;
  a2Summary?: Record<string, unknown> | null;
  a3Summary?: Record<string, unknown> | null;
  a1a3Summary?: Record<string, unknown> | null;
}

/** Prefer contributor submit time; fall back to record creation for legacy data. */
export function formatExpertDatasetSubmittedOn(d: ExpertDataset): string {
  const anyD = d as ExpertDataset & { submitted_date?: string | null; created_at?: string | null };
  const raw = d.submittedDate ?? anyD.submitted_date;
  if (raw != null && raw !== "") {
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
  }
  const created = d.createdAt ?? anyD.created_at;
  if (created != null && created !== "") {
    const dt = new Date(created);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
  }
  return "—";
}

/** Type definition — Pagination. */
export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface ExpertDatasetsByMaterialResponse {
  success: boolean;
  byMaterial?: {
    count?: number;
    data?: ExpertDataset[];
  };
  // defensive fallbacks
  data?: ExpertDataset[];
}

interface ExpertSubmissionsByAssignedMaterialResponse {
  success: boolean;
  data: ExpertDataset[];
  pagination?: Pagination;
}

/** Exported function — get Expert Datasets By Material. */
export async function getExpertDatasetsByMaterial(): Promise<ExpertDataset[]> {
  const res = await axiosClient.get<ExpertDatasetsByMaterialResponse>(API_URLS.EXPERT.DATASETS, {
    params: { type: "byMaterial" },
  });

  if (!res.data?.success) return [];
  return res.data.byMaterial?.data ?? res.data.data ?? [];
}

/** Exported function — get Expert Submissions By Assigned Material. */
export async function getExpertSubmissionsByAssignedMaterial(params: {
  page: number;
  pageSize: number;
}): Promise<{ data: ExpertDataset[]; pagination: Pagination | null }> {
  const res = await axiosClient.get<ExpertSubmissionsByAssignedMaterialResponse>(
    API_URLS.EXPERT.SUBMISSIONS_BY_ASSIGNED_MATERIAL,
    { params }
  );

  if (!res.data?.success) return { data: [], pagination: null };
  return { data: res.data.data || [], pagination: res.data.pagination ?? null };
}

/** Type definition — Expert Dataset Table Row. */
export interface ExpertDatasetTableRow {
  submissionId: string;
  submissionCode: string;
  submittedOn: string;
  subMaterial?: string | null;
  embodiedCarbon?: number | null;
  embodiedCarbonUnit?: string | null;
  contributorName?: string | null;
  contributorEmail?: string | null;
  materialCode?: string | null;
  dataReview?: string | null;
  dataCompleteness?: string | null;
  dataAcquisition?: string | null;
  temporal?: string | null;
  geographical?: string | null;
  technological?: string | null;
  manufacturer?: string | null;
  dataSource?: string | null;
}

/** Exported function — map Expert Dataset To Table Row. */
export function mapExpertDatasetToTableRow(d: ExpertDataset): ExpertDatasetTableRow {
  const bg = d.background;
  const startYear = bg?.collectionStartYear ?? null;
  const endYear = bg?.collectionEndYear ?? null;
  const temporal =
    startYear && endYear ? `${startYear}–${endYear}` : startYear || endYear || null;
  const { embodiedCarbon, embodiedCarbonUnit } = extractA1A3EmbodiedCarbon(d.a1a3Summary);

  return {
    submissionId: d.id,
    submissionCode: d.submissionCode || d.id,
    submittedOn: formatExpertDatasetSubmittedOn(d),
    subMaterial:
      bg?.subMaterialIdName ||
      (typeof bg?.subMaterialId === "number" ? `SubMaterial #${bg.subMaterialId}` : null),
    embodiedCarbon,
    embodiedCarbonUnit,
    contributorName: d.contributor?.name ?? null,
    contributorEmail: d.contributor?.email ?? null,
    materialCode: bg?.material?.code ?? null,
    dataReview: bg?.dataReview ?? null,
    dataCompleteness: bg?.dataCompleteness ?? null,
    dataAcquisition: bg?.dataAcquisition ?? null,
    temporal,
    geographical: formatBackgroundGeoFromApi(bg ?? null),
    technological:
      bg?.technologyIdName ||
      (typeof bg?.technologyId === "number" ? `Technology #${bg.technologyId}` : null),
    manufacturer:
      typeof bg?.manufacturerId === "number" ? `Manufacturer #${bg.manufacturerId}` : null,
    dataSource: d.contributorProfile?.dataSource ?? null,
  };
}

/** Type definition — Chart Slot Code Entry. */
export type ChartSlotCodeEntry = {
  id: string;
  code: string;
};

/** Type definition — Chart Slot Code Groups. */
export type ChartSlotCodeGroups = {
  allEntries: ChartSlotCodeEntry[];
  reviewQueueEntries: ChartSlotCodeEntry[];
};

/** Split a chart column's submissions into all datasets vs review-queue entries. */
export function splitChartSlotCodesByReviewQueue(
  selection: CementBoxPlotSlotSelection,
  reviewQueueIds: readonly string[]
): ChartSlotCodeGroups {
  const reviewSet = new Set(reviewQueueIds.map(String));
  const allEntries: ChartSlotCodeEntry[] = [];
  const reviewQueueEntries: ChartSlotCodeEntry[] = [];

  selection.datasetIds.forEach((id, index) => {
    const entry: ChartSlotCodeEntry = {
      id: String(id),
      code: selection.submissionCodes[index] ?? String(id),
    };
    allEntries.push(entry);
    if (reviewSet.has(String(id))) {
      reviewQueueEntries.push(entry);
    }
  });

  return { allEntries, reviewQueueEntries };
}

/** Exported function — toggle Expert Chart Slot Selection. */
export function toggleExpertChartSlotSelection(
  prev: CementBoxPlotSlotSelection | null,
  detail: CementBoxPlotSlotSelection
): CementBoxPlotSlotSelection | null {
  if (!detail.datasetIds.length) return prev;
  if (prev && prev.slotKey === detail.slotKey && prev.metric === detail.metric) {
    return null;
  }
  return detail;
}

/** Exported function — filter Rows By Chart Slot. */
export function filterRowsByChartSlot<T extends { submissionId: string }>(
  rows: T[],
  chartSlotSelection: CementBoxPlotSlotSelection | null | undefined
): T[] {
  if (!chartSlotSelection?.datasetIds.length) return rows;
  const idSet = new Set(chartSlotSelection.datasetIds.map(String));
  return rows.filter((row) => idSet.has(String(row.submissionId)));
}

/** Exported function — filter Expert Dataset Table Rows. */
export function filterExpertDatasetTableRows<T extends ExpertDatasetTableRow>(
  rows: T[],
  searchTerm: string
): T[] {
  const query = searchTerm.trim().toLowerCase();
  if (!query) return rows;

  return rows.filter((row) => {
    const submissionCode = row.submissionCode?.toLowerCase() || "";
    const contributorName = row.contributorName?.toLowerCase() || "";
    const contributorEmail = row.contributorEmail?.toLowerCase() || "";
    const materialCode = row.materialCode?.toLowerCase() || "";
    return (
      submissionCode.includes(query) ||
      contributorName.includes(query) ||
      contributorEmail.includes(query) ||
      materialCode.includes(query)
    );
  });
}

/** Exported function — extract A1A3Embodied Carbon. */
export function extractA1A3EmbodiedCarbon(
  a1a3Summary?: Record<string, unknown> | null
): {
  embodiedCarbon: number | null;
  embodiedCarbonUnit: string | null;
} {
  const summary = a1a3Summary ?? {};
  const rawValue = summary.embodiedCarbonValue ?? summary.embodied_carbon_value;
  let embodiedCarbon: number | null = null;
  if (rawValue != null && rawValue !== "") {
    const n = Number(rawValue);
    embodiedCarbon = Number.isFinite(n) ? n : null;
  }

  const rawUnit = summary.embodiedCarbonUnit ?? summary.embodied_carbon_unit;
  const embodiedCarbonUnit =
    rawUnit != null && String(rawUnit).trim()
      ? formatCompoundUnitSymbol(String(rawUnit).trim())
      : null;

  return { embodiedCarbon, embodiedCarbonUnit };
}

/** Exported function — enrich Expert Datasets. */
export async function enrichExpertDatasets(raw: ExpertDataset[]): Promise<ExpertDataset[]> {
  // Enrich subMaterialId -> name so UI doesn't show "SubMaterial #id"
  const uniqueSubMaterialIds = Array.from(
    new Set(
      (raw || [])
        .map((d) => d.background?.subMaterialId)
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v))
    )
  );

  const subMaterialNameById = new Map<number, string>();
  if (uniqueSubMaterialIds.length > 0) {
    await Promise.all(
      uniqueSubMaterialIds.map(async (id) => {
        try {
          const r = await axiosClient.get(API_URLS.SUB_MATERIALS.BY_ID(id));
          const data = r.data?.data ?? r.data;
          const name = data?.name ?? data?.subMaterialName ?? data?.code ?? null;
          if (typeof name === "string" && name.trim()) {
            subMaterialNameById.set(id, name.trim());
          }
        } catch {
          // ignore individual lookups
        }
      })
    );
  }

  // Enrich technologyId -> name so charts don't show "Technology #id"
  const uniqueMaterialIdsForTech = Array.from(
    new Set(
      (raw || [])
        .map((d) => d.background?.materialId)
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v))
    )
  );

  const technologyNameByMaterialAndId = new Map<number, Map<number, string>>();
  if (uniqueMaterialIdsForTech.length > 0) {
    await Promise.all(
      uniqueMaterialIdsForTech.map(async (matId) => {
        try {
          const r = await axiosClient.get(API_URLS.TECHNOLOGICAL_SPECIFICITIES.BY_MATERIAL(matId));
          const payload = Array.isArray(r.data)
            ? r.data
            : Array.isArray(r.data?.data)
              ? r.data.data
              : [];

          const map = new Map<number, string>();
          for (const item of payload as any[]) {
            const id = Number(item?.id ?? item?.technologyId);
            if (!Number.isFinite(id)) continue;
            const name =
              item?.technologyName ??
              item?.name ??
              item?.label ??
              item?.value ??
              item?.code ??
              null;
            if (typeof name === "string" && name.trim()) map.set(id, name.trim());
          }
          technologyNameByMaterialAndId.set(matId, map);
        } catch {
          // ignore individual lookups
        }
      })
    );
  }

  return (raw || []).map((d) => {
    const subId = d.background?.subMaterialId;
    const subMaterialName =
      typeof subId === "number" ? subMaterialNameById.get(subId) ?? null : null;

    const bg = d.background;
    const techId = bg?.technologyId;
    const matId = bg?.materialId;
    const techName =
      typeof matId === "number" && typeof techId === "number"
        ? technologyNameByMaterialAndId.get(matId)?.get(techId) ?? null
        : null;

    return {
      ...d,
      background: bg
        ? {
            ...bg,
            subMaterialIdName: bg.subMaterialIdName ?? subMaterialName,
            technologyIdName: bg.technologyIdName ?? techName,
          }
        : bg,
    };
  });
}

